import { useNavigate } from 'react-router-dom';
import DashboardCard, { EmptyState } from './DashboardCard';
import type { BedsideDeviceHealthSummary } from '../../api/bedsideApi';

interface Props {
  data: BedsideDeviceHealthSummary;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function BedsideDevicesCard({ data, ...dragProps }: Props) {
  const navigate = useNavigate();

  // Anything without a positive total is "none registered": an empty or
  // malformed health answer (the e2e stub's `[]`, a 204) drew "Active:" with
  // no number after it.
  if (!data || !(Number(data.total) > 0)) {
    return (
      <DashboardCard icon="bi-tv" title="Bedside Devices" {...dragProps}>
        <EmptyState message="No bedside devices registered" />
      </DashboardCard>
    );
  }

  return (
    <DashboardCard icon="bi-tv" title="Bedside Devices" count={data.total} {...dragProps}>
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => navigate('/settings?tab=bedside')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') navigate('/settings?tab=bedside'); }}
      >
        <div style={{ display: 'flex', gap: 16, padding: '6px 10px', flexWrap: 'wrap', fontSize: 'var(--font-size-base)' }}>
          <StatBadge label="Active" value={data.active} color="#16a34a" />
          {data.expiringSoon > 0 && (
            <StatBadge label="Expiring Soon" value={data.expiringSoon} color="#eab308" />
          )}
          {data.expired > 0 && (
            <StatBadge label="Expired" value={data.expired} color="#ef4444" />
          )}
        </div>

        {/* Warnings */}
        {data.expired > 0 && (
          <div style={warningStyle('#fef2f2', 'var(--color-negative)')}>
            {data.expired} device{data.expired !== 1 ? 's have' : ' has'} expired - renew or delete in settings
          </div>
        )}
        {data.expiringSoon > 0 && (
          <div style={warningStyle('#fffbeb', '#d97706')}>
            {data.expiringSoon} device{data.expiringSoon !== 1 ? 's expire' : ' expires'} within 30 days
          </div>
        )}
        {(data.duplicateRoomBeds?.length ?? 0) > 0 && (
          <div style={warningStyle('#fefce8', '#a16207')}>
            Duplicate devices in: {data.duplicateRoomBeds.join(', ')}
          </div>
        )}

        {data.expired === 0 && data.expiringSoon === 0 && (data.duplicateRoomBeds?.length ?? 0) === 0 && (
          <div style={{ padding: '0 10px 6px', color: 'var(--text-secondary)' }}>
            All devices healthy
          </div>
        )}
      </div>
    </DashboardCard>
  );
}

function StatBadge({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}:</span>{' '}
      <span style={{ color, fontWeight: 600 }}>{value}</span>
    </span>
  );
}

function warningStyle(bg: string, color: string): React.CSSProperties {
  return {
    padding: '4px 10px', margin: '0 10px 4px', borderRadius: 2,
    fontSize: 'var(--font-size-base)',
    background: bg, color,
  };
}
